import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { jobsService } from "../services/jobs";
import { ApiError } from "../services/api";
import ManualJobEntryForm from "../components/ManualJobEntryForm";
import { Button } from "@/components/ui/Button";
import type { JobManualCreateRequest } from "../types";

export default function JobCreatePage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);

  async function handleUrlSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const job = await jobsService.createFromUrl({ source_url: url.trim() });
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.detail === "manual_entry_required") {
          setShowManual(true);
        } else {
          setError(err.detail);
        }
      } else {
        setError("Failed to fetch job posting. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleManualSubmit(data: JobManualCreateRequest) {
    setLoading(true);
    setError(null);
    try {
      const job = await jobsService.createManual({ ...data, source_url: url.trim() || data.source_url });
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError("Failed to create job. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  if (showManual) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-6">
        <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-slate-100">Add Job Manually</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          The job posting couldn't be fetched automatically. Please paste the job details below.
        </p>
        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md px-4 py-3 mb-4 text-error text-sm">
            {error}
          </div>
        )}
        <ManualJobEntryForm
          initialUrl={url}
          onSubmit={handleManualSubmit}
          loading={loading}
          onCancel={() => setShowManual(false)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-10 px-6">
      <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-slate-100">Add New Job</h1>
      <p className="text-slate-500 dark:text-slate-400 mb-6">
        Paste a job posting URL and we'll automatically extract the details.
      </p>

      <form onSubmit={handleUrlSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="job-url" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
            Job Posting URL
          </label>
          <input
            id="job-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/jobs/software-engineer"
            required
            className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-md text-base bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
          />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md px-4 py-3 text-error text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={loading || !url.trim()} loading={loading}>
            {loading ? "Fetching…" : "Fetch Job"}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setShowManual(true)}>
            Enter Manually
          </Button>
        </div>
      </form>
    </div>
  );
}
